import { useApiClient } from "@/hooks/useApiClient";
import { type GeoSpaceChiban, type MapServiceContractResponse } from "../types";

interface GeospaceSearchResponse {
  status: string;
  error_message: string;
  chiban: GeoSpaceChiban[];
}

interface GeospaceReverseResponse {
  status: string;
  error_message: string;
  chiban: GeoSpaceChiban;
}

interface GeospaceRangeReverseResponse {
  status: string;
  error_message: string;
  chiban: GeoSpaceChiban[];
}

interface GeospaceReverseFreeResponse {
  lat: number | null;
  lng: number | null;
  address: string;
}

interface GeoSpaceSearchRequestParams {
  chiban: string;
  is_match?: boolean;
}

interface GeoSpaceReverseRequestParams {
  address: string;
}

interface GeoSpaceReverseRequestFreeParams
  extends GeoSpaceReverseRequestParams {}

interface GeoSpaceReverseByLatLngRequestParams {
  lat: number;
  lng: number;
}

interface GeoSpaceReverseByPolygonRequestParams {
  polygon: string | undefined;
}

interface GeoSpaceReverseByCircleRequestParams {
  radius: number;
  lat: number;
  lng: number;
}

interface GeoSpaceApi {
  geospaceSearch: (
    d: string,
    isMatch?: boolean
  ) => Promise<GeoSpaceChiban | undefined>;
  geospaceReverse: (d: string) => Promise<GeoSpaceChiban | undefined>;
  geospaceLegalAffairsSearch: (
    d: string,
    isMatch?: boolean
  ) => Promise<GeoSpaceChiban | undefined>;
  geospaceLegalAffairsReverse: (
    d: string
  ) => Promise<GeoSpaceChiban | undefined>;
  geospaceReverseFree: (
    d: string
  ) => Promise<GeospaceReverseFreeResponse | undefined>;
  geospaceReverseByLatLng: (
    lat: number,
    lng: number
  ) => Promise<GeoSpaceChiban | undefined>;
  geospaceReverseByPolygon: (
    polygon: string | undefined,
    signal?: AbortSignal
  ) => Promise<GeoSpaceChiban[] | undefined>;
  geospaceReverseByCircle: (
    radius: number,
    lat: number,
    lng: number,
    signal?: AbortSignal
  ) => Promise<GeoSpaceChiban[] | undefined>;
  geospaceGetMapServiceContract: () => Promise<
    MapServiceContractResponse | undefined
  >;
  geospaceLegalAffairsReverseByLatlng: (
    lat: number,
    lng: number
  ) => Promise<GeoSpaceChiban | undefined>;
  geospaceLegalAffairsReverseByPolygon: (
    polygon: string | undefined,
    signal?: AbortSignal
  ) => Promise<GeoSpaceChiban[] | undefined>;
  geospaceLegalAffairsReverseByCircle: (
    radius: number,
    lat: number,
    lng: number,
    signal?: AbortSignal
  ) => Promise<GeoSpaceChiban[] | undefined>;
}

export const useGeoSpaceApi = (): GeoSpaceApi => {
  const { apiClient } = useApiClient();

  const geospaceSearch = async (
    d: string,
    isMatch?: boolean
  ): Promise<GeoSpaceChiban | undefined> => {
    const params: GeoSpaceSearchRequestParams = {
      chiban: d,
      ...(isMatch !== undefined && { is_match: isMatch }),
    };
    try {
      const response = await apiClient.get<GeospaceSearchResponse>(
        "/geospace/search",
        {
          params,
        }
      );
      if (response.data.status === "success") {
        return response.data.chiban[0];
      } else {
        return undefined;
      }
    } catch (e) {
      return undefined;
    }
  };

  const geospaceReverse = async (
    d: string
  ): Promise<GeoSpaceChiban | undefined> => {
    const params: GeoSpaceReverseRequestParams = {
      address: d,
    };
    const response = await apiClient.get<GeospaceReverseResponse>(
      "/geospace/reverse",
      {
        params,
      }
    );
    if (response.data.status === "success") {
      return response.data.chiban;
    }
    // 検索結果が0だった場合
    if (response.data.status === "") {
      return undefined;
    }
    throw new Error();
  };

  const geospaceLegalAffairsSearch = async (
    d: string,
    isMatch?: boolean
  ): Promise<GeoSpaceChiban | undefined> => {
    const params: GeoSpaceSearchRequestParams = {
      chiban: d,
      ...(isMatch !== undefined && { is_match: isMatch }),
    };
    try {
      const response = await apiClient.get<GeospaceSearchResponse>(
        "/geospace/legal-affairs/search",
        {
          params,
        }
      );
      if (response.data.status === "success") {
        return response.data.chiban[0];
      } else {
        return undefined;
      }
    } catch (e) {
      return undefined;
    }
  };

  const geospaceLegalAffairsReverse = async (
    d: string
  ): Promise<GeoSpaceChiban | undefined> => {
    const params: GeoSpaceReverseRequestParams = {
      address: d,
    };
    const response = await apiClient.get<GeospaceReverseResponse>(
      "/geospace/legal-affairs/reverse",
      {
        params,
      }
    );
    if (response.data.status === "success") {
      return response.data.chiban;
    }
    // 検索結果が0だった場合
    if (response.data.status === "") {
      return undefined;
    }
    throw new Error();
  };

  // 地図フリープラン向け、住居表示から座標を取得するAPIを呼び出す
  const geospaceReverseFree = async (
    d: string
  ): Promise<GeospaceReverseFreeResponse | undefined> => {
    const params: GeoSpaceReverseRequestFreeParams = {
      address: d,
    };
    const response = await apiClient.get<{
      lat: number | null;
      lon: number | null;
      address: string;
    }>("/geospace/reverse-free", {
      params,
    });
    if (response.status === 200) {
      return {
        lat: response.data.lat,
        lng: response.data.lon,
        address: response.data.address,
      };
    }

    throw new Error();
  };

  const _reverseByLatLng = async (
    url: string,
    lat: number,
    lng: number
  ): Promise<GeoSpaceChiban | undefined> => {
    const params: GeoSpaceReverseByLatLngRequestParams = { lat, lng };
    try {
      const response = await apiClient.get<GeospaceReverseResponse>(url, {
        params,
      });
      if (response.data.status === "success") {
        return response.data.chiban;
      }
      return undefined;
    } catch (e) {
      return undefined;
    }
  };

  const geospaceReverseByLatLng = async (
    lat: number,
    lng: number
  ): Promise<GeoSpaceChiban | undefined> => {
    return await _reverseByLatLng("/geospace/reverse-latlng", lat, lng);
  };

  const _reverseByPolygon = async (
    url: string,
    polygon: string | undefined,
    signal?: AbortSignal
  ): Promise<GeoSpaceChiban[] | undefined> => {
    const params: GeoSpaceReverseByPolygonRequestParams = { polygon };

    try {
      const response = await apiClient.get<GeospaceRangeReverseResponse>(url, {
        params,
        signal,
      });
      // ヒットなしは status が空文字列で返るため、status === "success" を成否の判定に
      // 使わない。HTTP 200 まで来たら chiban の中身（0 件を含む）をそのまま返す。
      return response.data.chiban ?? [];
    } catch (e) {
      return undefined;
    }
  };

  const _reverseByCircle = async (
    url: string,
    radius: number,
    lat: number,
    lng: number,
    signal?: AbortSignal
  ): Promise<GeoSpaceChiban[] | undefined> => {
    // radius は整数のみ受け付ける（小数を渡すと 400）。Leaflet の getRadius() は
    // 小数を返すため、ここで切り上げて丸める。
    const params: GeoSpaceReverseByCircleRequestParams = {
      radius: Math.ceil(radius),
      lat,
      lng,
    };

    try {
      const response = await apiClient.get<GeospaceRangeReverseResponse>(url, {
        params,
        signal,
      });
      // ヒットなしは status が空文字列で返る（_reverseByPolygon と同じ理由）
      return response.data.chiban ?? [];
    } catch (e) {
      return undefined;
    }
  };

  const geospaceReverseByPolygon = async (
    polygon: string | undefined,
    signal?: AbortSignal
  ): Promise<GeoSpaceChiban[] | undefined> =>
    await _reverseByPolygon("/geospace/reverse-polygon", polygon, signal);

  const geospaceReverseByCircle = async (
    radius: number,
    lat: number,
    lng: number,
    signal?: AbortSignal
  ): Promise<GeoSpaceChiban[] | undefined> =>
    await _reverseByCircle(
      "/geospace/reverse-circle",
      radius,
      lat,
      lng,
      signal
    );

  const geospaceLegalAffairsReverseByPolygon = async (
    polygon: string | undefined,
    signal?: AbortSignal
  ): Promise<GeoSpaceChiban[] | undefined> =>
    await _reverseByPolygon(
      "/geospace/legal-affairs/reverse-polygon",
      polygon,
      signal
    );

  const geospaceLegalAffairsReverseByCircle = async (
    radius: number,
    lat: number,
    lng: number,
    signal?: AbortSignal
  ): Promise<GeoSpaceChiban[] | undefined> =>
    await _reverseByCircle(
      "/geospace/legal-affairs/reverse-circle",
      radius,
      lat,
      lng,
      signal
    );

  const geospaceGetMapServiceContract = async (): Promise<
    MapServiceContractResponse | undefined
  > => {
    try {
      const response = await apiClient.get<MapServiceContractResponse>(
        "/user/map-service-contract"
      );
      return {
        geospace: response.data.geospace,
        zenrin: response.data.zenrin,
      };
    } catch (e) {
      return undefined;
    }
  };

  const geospaceLegalAffairsReverseByLatlng = async (
    lat: number,
    lng: number
  ): Promise<GeoSpaceChiban | undefined> => {
    return await _reverseByLatLng(
      "/geospace/legal-affairs/reverse-latlng",
      lat,
      lng
    );
  };

  return {
    geospaceSearch,
    geospaceReverse,
    geospaceLegalAffairsSearch,
    geospaceLegalAffairsReverse,
    geospaceReverseFree,
    geospaceReverseByLatLng,
    geospaceReverseByPolygon,
    geospaceReverseByCircle,
    geospaceGetMapServiceContract,
    geospaceLegalAffairsReverseByLatlng,
    geospaceLegalAffairsReverseByPolygon,
    geospaceLegalAffairsReverseByCircle,
  };
};
